Observable from @danielx/observable

{ clamp, MidiEvent } from @danielx/lib

/**
Connect and map MIDI input into notes on the musical staff.

Can receive MIDI events from multiple devices and dispatch them to the synth.
*/
export MidiInput := <Base extends Constructor<{}>>(S: Base) ->
  class MidiInput extends S
    receiveMidiEvent: (event: MIDIMessageEvent) => void
    initMidiInput: () => void

    recordingBeatAdvance = Observable 1
    recordingMidiInput = Observable false
    activeMidiDeviceIndex = Observable -1
    activeMidiDeviceInput = =>
      midiDevices()[@activeMidiDeviceIndex()]

    midiDevices = midiDevices

    constructor(...args: any[])
      super ...args

      startTimeStamp .= performance.now()
      eventBuffer: MIDIMessageEvent[] := []
      startTime .= 0

      /** Update active midi device when device list changes */
      updateActiveDevice := =>
        devices := midiDevices()
        // Hardware auto-selects on connect like before; the virtual keyboard
        // row is always present, so None (-1) stays the floor when it's the
        // only entry — typing must not become notes until it's chosen
        hasHardware := devices.some (d) => d !== keyboardMidiDevice
        min := if hasHardware then 0 else -1
        @activeMidiDeviceIndex clamp @activeMidiDeviceIndex(), min, devices.length - 1

      midiDevices.observe updateActiveDevice
      updateActiveDevice()

      /** Tracker-style typing only captures keys while the virtual keyboard
      device is the selected input */
      updateKeyboardCapture := =>
        keyboardMidiActive @activeMidiDeviceInput() is keyboardMidiDevice
      @activeMidiDeviceIndex.observe updateKeyboardCapture
      midiDevices.observe updateKeyboardCapture
      updateKeyboardCapture()

      // Keyboard notes land on the channel being edited
      setKeyboardMidiChannel => (@ as any).activeChannel?.() ?? 0

      // Pause/Break flips between the virtual keyboard and whatever device
      // was selected before it (None included), so typing can jump between
      // hotkeys and notes without opening the mode modal
      previousDeviceIndex .= -1
      setKeyboardMidiToggle =>
        devices := midiDevices()
        kbIndex := devices.indexOf keyboardMidiDevice
        if @activeMidiDeviceIndex() is kbIndex
          @activeMidiDeviceIndex clamp previousDeviceIndex, -1, devices.length - 1
        else
          previousDeviceIndex = @activeMidiDeviceIndex()
          @activeMidiDeviceIndex kbIndex

      @initMidiInput = =>
        @on "initPlay", =>
          eventBuffer.length = 0
          // a DOMHighResTimeStamp, measured in milliseconds since the browser context was created
          startTimeStamp = performance.now()
          // seconds into the song where playback is starting
          startTime = @currentTime()

        @on "stop", =>
          if @recordingMidiInput() and eventBuffer.length
            song := @song

            midiEvents := eventBuffer.map (e) =>
              {timeStamp, data} := e

              // time of event in seconds within the song, mapped through the
              // audio output clock when available so what the player heard is
              // what gets written; the performance-clock difference is the
              // fallback for contexts without getOutputTimestamp
              s := @songTimeAtPerformanceTime?.(timeStamp) ??
                startTime + (timeStamp - startTimeStamp) / 1000

              tick := song.tickAtTime(s)
              new MidiEvent tick, data

            // Add events and add an undo state
            song.addEvents midiEvents
            @pushState @getState()

            eventBuffer.length = 0

      @receiveMidiEvent = (event: MIDIMessageEvent) =>
        { currentTarget, data, timeStamp } := event

        // TODO: accurate time stamping

        return unless data
        return unless currentTarget is @activeMidiDeviceInput()

        // using a dummy event for the convenience methods
        { type } := new MidiEvent(0, data)

        // note by note input and advance
        if !@playing() and @recordingMidiInput()
          switch type
            when "noteOn" // add note and advance playhead
              beat := @currentBeat()
              duration := @defaultDuration()
              beatAdvance := @recordingBeatAdvance()
              [e, n, v] := data
              c := e & 0x0f

              // Add note to score
              @addNote c, n, v, beat, duration

              // Play note sound
              tempo := @song.tempoAtBeat(beat)
              bpm := 60000000 / tempo
              @playNote c, n, v
              dt := duration / bpm * 60
              @releaseNote c, n, v, dt

              @currentBeat(beat + beatAdvance)
        else // live playback
          // TODO: a different method on the player that can handle this whole if else
          // Practice mode reroutes the input onto the practiced channel so the
          // player hears the part's own instrument
          @dispatchImmediateMidiData @practiceRouteMidiData?.(data) ?? data

          // Gather all the events to be merged into the track later
          if @recordingMidiInput()
            eventBuffer.push event

      addMidiEventListener @receiveMidiEvent

export default MidiInput

// DOM Bindings

/** Handlers to receive midi events */
handlers: ((e: MIDIMessageEvent) => void)[] := []

/** Add a handler to listen to all midi events from all connected devices */
export addMidiEventListener := (handler: (e: MIDIMessageEvent) => void) =>
  handlers.push handler

/** Remove a handler from listening to all midi events */
export removeMidiEventListener := (handler: (e: MIDIMessageEvent) => void) =>
  i := handlers.indexOf handler
  if i >= 0
    handlers.splice i, 1

// Computer keyboard as a virtual MIDI device: tracker-style note entry on
// hardware key positions (KeyboardEvent.code), so the layout is identical on
// QWERTY, AZERTY, or Dvorak. Two manuals like FastTracker: the Z row is the
// base octave, the Q row one octave up, with the row above each holding the
// sharps. It's always in the device list, selectable from the same dropdown
// as hardware inputs, and its events flow through the normal handler path so
// step entry, live play, recording, and practice all work unchanged.

/** Virtual device selectable alongside hardware inputs */
export keyboardMidiDevice := Object.assign(new EventTarget(),
  name: "Computer Keyboard"
  manufacturer: "tracker layout"
) as unknown as MIDIInput

/** physical key -> semitone offset from the base octave's C */
KEYBOARD_NOTE_OFFSETS: Record<string, number> :=
  // bottom two rows
  KeyZ: 0, KeyS: 1, KeyX: 2, KeyD: 3, KeyC: 4, KeyV: 5, KeyG: 6
  KeyB: 7, KeyH: 8, KeyN: 9, KeyJ: 10, KeyM: 11
  Comma: 12, KeyL: 13, Period: 14, Semicolon: 15, Slash: 16
  // top two rows, one octave up
  KeyQ: 12, Digit2: 13, KeyW: 14, Digit3: 15, KeyE: 16, KeyR: 17
  Digit5: 18, KeyT: 19, Digit6: 20, KeyY: 21, Digit7: 22, KeyU: 23
  KeyI: 24, Digit9: 25, KeyO: 26, Digit0: 27, KeyP: 28

KEYBOARD_VELOCITY := 100

/** Base octave for the Z row; 4 puts KeyZ at middle C */
export keyboardOctave := Observable 4

/** Set while a player has the virtual keyboard selected as its input */
keyboardMidiActive := Observable false

/** Where keyboard notes land; the player wires this to its active channel */
keyboardMidiChannel .= (): number => 0
export setKeyboardMidiChannel := (provider: () => number) =>
  keyboardMidiChannel = provider

/** Registered by the player so Pause/Break can flip its selected device */
toggleKeyboardMidi .= undefined as (() => void) | undefined
export setKeyboardMidiToggle := (toggle: () => void) =>
  toggleKeyboardMidi = toggle

/** notes held down, by physical key, so octave/channel changes mid-hold
still release the right note */
heldKeyboardNotes := new Map<string, { note: number, channel: number }>()

emitKeyboardMidi := (data: Uint8Array) =>
  event := { data, timeStamp: performance.now(), currentTarget: keyboardMidiDevice } as unknown as MIDIMessageEvent
  handlers.forEach (h) => h event

releaseAllKeyboardNotes := =>
  heldKeyboardNotes.forEach ({ note, channel }) =>
    emitKeyboardMidi new Uint8Array [0x80 | channel, note, 0]
  heldKeyboardNotes.clear()

keyboardMidiActive.observe (active: boolean) =>
  releaseAllKeyboardNotes() unless active

/** true when the key press belongs to something else: a text field or modal */
keyboardCaptureBlocked := (e: KeyboardEvent) =>
  return true if e.ctrlKey or e.metaKey or e.altKey
  return true if (e.target as Element)?.closest? "input, textarea, select, [contenteditable]"
  return true if document.querySelector "dialog[open]"
  false

consumeKey := (e: KeyboardEvent) =>
  e.preventDefault()
  e.stopPropagation()

// Capture phase so note keys win over the single-letter editor hotkeys
// (Mousetrap listens on document in the bubble phase) while active
document.addEventListener "keydown", (e: KeyboardEvent) =>
  { code } := e

  // Pause/Break or Ctrl+Space (for laptops without a Pause key) toggles the
  // virtual keyboard on and off from anywhere, so it works while inactive too
  if code is "Pause" or
     (code is "Space" and e.ctrlKey and not (e.metaKey or e.altKey or e.shiftKey))
    unless e.repeat
      toggleKeyboardMidi?.()
      consumeKey e
    return

  return unless keyboardMidiActive()
  return if keyboardCaptureBlocked e
  return if e.repeat

  switch code
    when "BracketLeft", "NumpadDivide"
      keyboardOctave Math.max 0, keyboardOctave() - 1
      consumeKey e
    when "BracketRight", "NumpadMultiply"
      keyboardOctave Math.min 8, keyboardOctave() + 1
      consumeKey e
    else
      return if e.shiftKey // shift+digit pattern hotkeys stay live
      offset := KEYBOARD_NOTE_OFFSETS[code]
      return unless offset?
      consumeKey e
      return if heldKeyboardNotes.has code
      note := (keyboardOctave() + 1) * 12 + offset
      return unless 0 <= note <= 127
      channel := keyboardMidiChannel() & 0x0f
      heldKeyboardNotes.set code, { note, channel }
      emitKeyboardMidi new Uint8Array [0x90 | channel, note, KEYBOARD_VELOCITY]
, true

document.addEventListener "keyup", (e: KeyboardEvent) =>
  held := heldKeyboardNotes.get e.code
  return unless held
  heldKeyboardNotes.delete e.code
  emitKeyboardMidi new Uint8Array [0x80 | held.channel, held.note, 0]
  consumeKey e
, true

// Keys released outside the window would otherwise stick
window.addEventListener "blur", releaseAllKeyboardNotes

/** List of all connected MIDI devices, the virtual keyboard always last */
midiDevices := Observable [keyboardMidiDevice]

/** initialize midi device list and event listeners */
init := =>
  try // Firefox is shitting the bed on this one
    midiAccess := await navigator.requestMIDIAccess()

    midiMessageHandler := (e: MIDIMessageEvent) =>
      handlers.forEach (h) => h e

    /** Update the list of MIDI devices and add/remove event listeners */
    updateMidiDevices := =>
      oldDevices := midiDevices()
      newDevices := Array.from(midiAccess.inputs.values())

      oldDevices.forEach (input: MIDIInput) ->
        if !newDevices.includes(input) and input !== keyboardMidiDevice
          input.removeEventListener "midimessage", midiMessageHandler
      newDevices.forEach (input) ->
        if !oldDevices.includes input
          input.addEventListener "midimessage", midiMessageHandler

      // the virtual keyboard dispatches straight to handlers, no listener
      midiDevices newDevices.concat [keyboardMidiDevice]

    midiAccess.onstatechange = updateMidiDevices
    updateMidiDevices()
  catch e
    console.error e

// Calling navigator.requestMIDIAccess() before the document is ready is flaky
if document.readyState is "complete"
  init()
else
  window.addEventListener "load", init
